import {
  decorateLeafletPathDualDash
} from './portal-dual-dash.js'

const createSeekTools = function ({
  L = window.L,
  clamp = function (value, min, max) {
    return Math.max(min, Math.min(max, value))
  },
  offsetLatLngByMeters = function () { return null },
  bearingBetweenLatLng = function () { return NaN },
  angleDiffDeg = function () { return NaN },
  axialDiffDeg = function () { return NaN },
  normalizeBearingDeg = function (v) { return Number(v) },
  roundToTenth = function (v) { return Math.round(Number(v) * 10) / 10 },
  fnGetMap = function () { return null },
  fnGetMissionState = function () { return null },
  fnGetImportKmlFilename = function () { return '' },
  fnGetDrawnItems = function () { return null },
  fnGetAxisUpBearingLast = function () { return null },
  fnCreateInsertedPoint = function () { return null },
  fnCaptureState = function () { return null },
  fnSyncMissionPlacemarkOrder = function () {},
  fnRenderImportLines = function () {},
  fnHistoryPush = function () {},
  fnSelectClear = function () {},
} = {}) {
  const fnMaxNumber = function (list) {
    if (!Array.isArray(list) || !list.length) return NaN
    let max = -Infinity
    for (let i = 0; i < list.length; i++) {
      const n = Number(list[i])
      if (!Number.isFinite(n)) continue
      if (n > max) max = n
    }
    return max
  }

  const fnMinNumber = function (list) {
    if (!Array.isArray(list) || !list.length) return NaN
    let min = Infinity
    for (let i = 0; i < list.length; i++) {
      const n = Number(list[i])
      if (!Number.isFinite(n)) continue
      if (n < min) min = n
    }
    return min
  }

  const buildSeekSegments = function (waypoints) {
    const map = fnGetMap()
    if (!map || !Array.isArray(waypoints) || waypoints.length < 2) return []
    const segments = []
    for (let i = 0; i < waypoints.length - 1; i++) {
      const from = waypoints[i]
      const to = waypoints[i + 1]
      if (!from || !to || !Array.isArray(from.latlng) || !Array.isArray(to.latlng)) continue
      const fromLatLng = L.latLng(from.latlng[0], from.latlng[1])
      const toLatLng = L.latLng(to.latlng[0], to.latlng[1])
      const distance = map.distance(fromLatLng, toLatLng)
      if (!(distance > 0)) continue
      const bearing = bearingBetweenLatLng(from.latlng[0], from.latlng[1], to.latlng[0], to.latlng[1])
      segments.push({
        from: fromLatLng,
        to: toLatLng,
        distance: distance,
        bearing: bearing
      })
    }
    return segments
  }

  const mergeSeekCollinearSegments = function (segments, options) {
    const opts = Object.assign({
      axialToleranceDeg: 8
    }, options || {})
    const map = fnGetMap()
    if (!map || !Array.isArray(segments) || !segments.length) return []
    const out = []
    let active = null
    const fnCommit = function () {
      if (!active) return
      out.push(active)
      active = null
    }
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i]
      if (!seg || !seg.from || !seg.to || !Number.isFinite(seg.distance) || !(seg.distance > 0) || !Number.isFinite(seg.bearing)) {
        fnCommit()
        continue
      }
      if (!active) {
        active = Object.assign({}, seg)
        continue
      }
      const dirDiff = axialDiffDeg(active.bearing, seg.bearing)
      if (dirDiff > opts.axialToleranceDeg) {
        fnCommit()
        active = Object.assign({}, seg)
        continue
      }
      const nextFrom = active.from
      const nextTo = seg.to
      const nextDistance = map.distance(nextFrom, nextTo)
      if (!(nextDistance > 0)) {
        fnCommit()
        active = Object.assign({}, seg)
        continue
      }
      active.to = nextTo
      active.distance = nextDistance
      active.bearing = bearingBetweenLatLng(nextFrom.lat, nextFrom.lng, nextTo.lat, nextTo.lng)
    }
    fnCommit()
    return out
  }

  const findSeekRouteBearing = function (segments, options) {
    const opts = Object.assign({
      detectSplitRows: true
    }, options || {})
    const sourceSegments = opts.detectSplitRows ? mergeSeekCollinearSegments(segments) : segments
    if (!Array.isArray(sourceSegments) || !sourceSegments.length) return NaN
    let maxDistance = -Infinity
    const fallbackSegments = []
    for (let i = 0; i < sourceSegments.length; i++) {
      const seg = sourceSegments[i]
      const distance = Number(seg && seg.distance)
      if (!Number.isFinite(distance) || !(distance > 0)) continue
      if (distance > maxDistance) maxDistance = distance
      if (fallbackSegments.length < 3) {
        fallbackSegments.push(seg)
        fallbackSegments.sort((a, b) => b.distance - a.distance)
      } else if (distance > fallbackSegments[fallbackSegments.length - 1].distance) {
        fallbackSegments[fallbackSegments.length - 1] = seg
        fallbackSegments.sort((a, b) => b.distance - a.distance)
      }
    }
    if (!(maxDistance > 0)) return NaN
    const longThreshold = Math.max(40, maxDistance * 0.6)
    const longSegments = []
    for (let i = 0; i < sourceSegments.length; i++) {
      const seg = sourceSegments[i]
      if (Number(seg && seg.distance) >= longThreshold) longSegments.push(seg)
    }
    const selectedSegments = longSegments.length ? longSegments : fallbackSegments
    if (!selectedSegments.length) return NaN
    let sumX = 0
    let sumY = 0
    for (let i = 0; i < selectedSegments.length; i++) {
      const seg = selectedSegments[i]
      const theta = seg.bearing * (Math.PI / 180)
      const weight = Math.max(1, seg.distance)
      sumX += Math.cos(theta * 2) * weight
      sumY += Math.sin(theta * 2) * weight
    }
    if (Math.abs(sumX) < 1e-9 && Math.abs(sumY) < 1e-9) {
      return selectedSegments[0].bearing
    }
    const axialRad = 0.5 * Math.atan2(sumY, sumX)
    const axialDeg = axialRad * (180 / Math.PI)
    return (axialDeg + 360) % 360
  }

  const buildSeekAxisLayerFromBearing = function (anchorLatLng, bearingDeg) {
    const map = fnGetMap()
    if (!map || !anchorLatLng || !Number.isFinite(bearingDeg)) return null
    const size = map.getSize()
    const margin = 24
    const xMin = margin
    const yMin = margin
    const xMax = Math.max(margin + 1, size.x - margin)
    const yMax = Math.max(margin + 1, size.y - margin)
    const anchorPointRaw = map.latLngToContainerPoint(anchorLatLng)
    const anchorPoint = L.point(
      clamp(anchorPointRaw.x, xMin, xMax),
      clamp(anchorPointRaw.y, yMin, yMax)
    )
    const rad = bearingDeg * (Math.PI / 180)
    let dx = Math.sin(rad)
    let dy = -Math.cos(rad)
    const len = Math.hypot(dx, dy)
    if (!(len > 0)) return null
    dx /= len
    dy /= len
    const tValues = []
    if (Math.abs(dx) > 1e-6) {
      const tLeft = (xMin - anchorPoint.x) / dx
      const yLeft = anchorPoint.y + (tLeft * dy)
      if (yLeft >= yMin && yLeft <= yMax) tValues.push(tLeft)
      const tRight = (xMax - anchorPoint.x) / dx
      const yRight = anchorPoint.y + (tRight * dy)
      if (yRight >= yMin && yRight <= yMax) tValues.push(tRight)
    }
    if (Math.abs(dy) > 1e-6) {
      const tTop = (yMin - anchorPoint.y) / dy
      const xTop = anchorPoint.x + (tTop * dx)
      if (xTop >= xMin && xTop <= xMax) tValues.push(tTop)
      const tBottom = (yMax - anchorPoint.y) / dy
      const xBottom = anchorPoint.x + (tBottom * dx)
      if (xBottom >= xMin && xBottom <= xMax) tValues.push(tBottom)
    }
    if (tValues.length < 2) return null
    const tStart = fnMinNumber(tValues)
    const tEnd = fnMaxNumber(tValues)
    if (!Number.isFinite(tStart) || !Number.isFinite(tEnd) || Math.abs(tStart - tEnd) < 1) return null
    const pStart = L.point(anchorPoint.x + (tStart * dx), anchorPoint.y + (tStart * dy))
    const pEnd = L.point(anchorPoint.x + (tEnd * dx), anchorPoint.y + (tEnd * dy))
    const llStart = map.containerPointToLatLng(pStart)
    const llEnd = map.containerPointToLatLng(pEnd)
    return decorateLeafletPathDualDash(L.polyline([llStart, llEnd], {
      color: '#0d6efd',
      weight: 3,
      dashArray: '6,6'
    }), { L: L })
  }

  const resolveSeekAxisUpBearing = function (axisLayer) {
    const map = fnGetMap()
    const latlngs = axisLayer && axisLayer.getLatLngs ? axisLayer.getLatLngs() : []
    if (!map || !latlngs || latlngs.length < 2) return NaN
    const first = latlngs[0]
    const last = latlngs[latlngs.length - 1]
    const pFirst = map.latLngToContainerPoint(first)
    const pLast = map.latLngToContainerPoint(last)
    let upFrom = first
    let upTo = last
    if (pFirst.y < pLast.y || (pFirst.y === pLast.y && pFirst.x > pLast.x)) {
      upFrom = last
      upTo = first
    }
    return bearingBetweenLatLng(upFrom.lat, upFrom.lng, upTo.lat, upTo.lng)
  }

  const buildAutoSeekAxisLayer = function (options) {
    const opts = Object.assign({
      detectSplitRows: true
    }, options || {})
    const missionState = fnGetMissionState()
    if (!missionState || !Array.isArray(missionState.waypoints)) return null
    const segments = buildSeekSegments(missionState.waypoints)
    if (!segments.length) return null
    const sourceSegments = opts.detectSplitRows ? mergeSeekCollinearSegments(segments) : segments
    if (!sourceSegments.length) return null
    const routeBearing = findSeekRouteBearing(sourceSegments, {
      detectSplitRows: false
    })
    if (!Number.isFinite(routeBearing)) return null
    const axisBearing = (routeBearing + 90) % 360
    let longestSegment = null
    for (let i = 0; i < sourceSegments.length; i++) {
      const seg = sourceSegments[i]
      if (!longestSegment || seg.distance > longestSegment.distance) longestSegment = seg
    }
    if (!longestSegment) return null
    const anchorLatLng = L.latLng(
      (longestSegment.from.lat + longestSegment.to.lat) / 2,
      (longestSegment.from.lng + longestSegment.to.lng) / 2
    )
    return buildSeekAxisLayerFromBearing(anchorLatLng, axisBearing)
  }

  const getSeekAxisUpBearingForSuggest = function (options) {
    const opts = Object.assign({
      detectSplitRows: true,
      ignoreAxisUpBearingLast: false
    }, options || {})
    const axisUpBearingLast = fnGetAxisUpBearingLast()
    if (!opts.ignoreAxisUpBearingLast && Number.isFinite(axisUpBearingLast)) {
      return normalizeBearingDeg(axisUpBearingLast)
    }
    const autoAxisLayer = buildAutoSeekAxisLayer({
      detectSplitRows: opts.detectSplitRows
    })
    if (autoAxisLayer) {
      const bearing = resolveSeekAxisUpBearing(autoAxisLayer)
      if (Number.isFinite(bearing)) {
        return normalizeBearingDeg(bearing)
      }
    }
    return NaN
  }

  const buildHeadingSuggestModel = function (options) {
    const axisBearing = getSeekAxisUpBearingForSuggest(options)
    if (!Number.isFinite(axisBearing)) return null
    const axisLabel = axisBearing.toFixed(1)
    const offsetMinus90 = normalizeBearingDeg(axisBearing - 90)
    const offsetMinus90Label = offsetMinus90.toFixed(1)
    const offsetPlus90 = normalizeBearingDeg(axisBearing + 90)
    const offsetPlus90Label = offsetPlus90.toFixed(1)
    const offsetPlus180 = normalizeBearingDeg(axisBearing + 180)
    const offsetPlus180Label = offsetPlus180.toFixed(1)
    return [
      { prefix: 'Tengely: ', value: axisLabel },
      { prefix: '-90: ', value: offsetMinus90Label },
      { prefix: '+90: ', value: offsetPlus90Label },
      { prefix: '+180: ', value: offsetPlus180Label }
    ]
  }

  const buildSeekAxisLayerForPoint = function (latlng, options) {
    if (!latlng || !Number.isFinite(latlng.lat) || !Number.isFinite(latlng.lng)) return null
    const axisBearing = getSeekAxisUpBearingForSuggest(options)
    if (!Number.isFinite(axisBearing)) return null
    return buildSeekAxisLayerFromBearing(L.latLng(latlng.lat, latlng.lng), axisBearing)
  }

  const createSeekAxisLayer = function (anchorLatLng, options) {
    const opts = Object.assign({
      detectSplitRows: true,
      ignoreAxisUpBearingLast: false,
      forceAutoAxis: false
    }, options || {})
    if (anchorLatLng && Number.isFinite(anchorLatLng.lat) && Number.isFinite(anchorLatLng.lng)) {
      const axisFromPoint = buildSeekAxisLayerForPoint(anchorLatLng, {
        detectSplitRows: opts.detectSplitRows,
        ignoreAxisUpBearingLast: opts.ignoreAxisUpBearingLast
      })
      if (axisFromPoint) return axisFromPoint
    }
    if (opts.forceAutoAxis) {
      return buildAutoSeekAxisLayer({
        detectSplitRows: opts.detectSplitRows
      })
    }
    return buildAutoSeekAxisLayer({
      detectSplitRows: opts.detectSplitRows
    })
  }

  const attachSeekAxisLayerToModal = function (modal, anchorLatLng, options) {
    if (!modal) return null
    const axisLayer = createSeekAxisLayer(anchorLatLng, options)
    const drawnItems = fnGetDrawnItems()
    if (axisLayer && drawnItems) {
      drawnItems.addLayer(axisLayer)
      modal.__axisLayer = axisLayer
    }
    return axisLayer
  }

  const buildSeekRowCandidates = function (axisUpBearing) {
    const map = fnGetMap()
    const missionState = fnGetMissionState()
    if (!map || !missionState || !Array.isArray(missionState.waypoints)) return []
    const points = missionState.waypoints
    if (points.length < 2) return []
    const perpCandidates = []
    let maxDist = -Infinity
    for (let i = 0; i < points.length - 1; i++) {
      const from = points[i]
      const to = points[i + 1]
      if (!from || !to || !Array.isArray(from.latlng) || !Array.isArray(to.latlng)) continue
      const fromLatLng = L.latLng(from.latlng[0], from.latlng[1])
      const toLatLng = L.latLng(to.latlng[0], to.latlng[1])
      const distance = map.distance(fromLatLng, toLatLng)
      if (!(distance > 0)) continue
      const bearing = bearingBetweenLatLng(from.latlng[0], from.latlng[1], to.latlng[0], to.latlng[1])
      const perpDiff = Math.abs(angleDiffDeg(bearing, axisUpBearing) - 90)
      if (perpDiff > 25) continue
      perpCandidates.push({
        fromIndex: i,
        toIndex: i + 1,
        fromLatLng: fromLatLng,
        toLatLng: toLatLng,
        distance: distance,
        bearing: bearing,
        midpoint: L.latLng((fromLatLng.lat + toLatLng.lat) / 2, (fromLatLng.lng + toLatLng.lng) / 2)
      })
      if (distance > maxDist) maxDist = distance
    }
    if (!perpCandidates.length) return []
    const longThreshold = Math.max(8, maxDist * 0.72)
    for (let i = 0; i < perpCandidates.length; i++) {
      perpCandidates[i].isLong = perpCandidates[i].distance >= longThreshold
    }
    return perpCandidates
  }

  const getImportWaypointsCenter = function () {
    const missionState = fnGetMissionState()
    if (!missionState || !Array.isArray(missionState.waypoints) || !missionState.waypoints.length) return null
    let minLat = Infinity
    let maxLat = -Infinity
    let minLng = Infinity
    let maxLng = -Infinity
    missionState.waypoints.forEach((pt) => {
      if (!pt || !Array.isArray(pt.latlng) || pt.latlng.length < 2) return
      const lat = parseFloat(pt.latlng[0])
      const lng = parseFloat(pt.latlng[1])
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
      if (lat < minLat) minLat = lat
      if (lat > maxLat) maxLat = lat
      if (lng < minLng) minLng = lng
      if (lng > maxLng) maxLng = lng
    })
    if (!Number.isFinite(minLat) || !Number.isFinite(maxLat) || !Number.isFinite(minLng) || !Number.isFinite(maxLng)) return null
    return L.latLng((minLat + maxLat) / 2, (minLng + maxLng) / 2)
  }

  const pickSeekRowCandidate = function (candidates, mode) {
    if (!Array.isArray(candidates) || !candidates.length) return null
    const longRows = candidates.filter((c) => c.isLong)
    const source = longRows.length ? longRows : candidates
    const ordered = source.slice().sort((a, b) => a.fromIndex - b.fromIndex)
    if (!ordered.length) return null
    return mode === 'last' ? ordered[ordered.length - 1] : ordered[0]
  }

  const estimateSeekCloneDistance = function (candidates, axisUpBearing) {
    const map = fnGetMap()
    if (!map || !Array.isArray(candidates) || candidates.length < 2 || !Number.isFinite(axisUpBearing)) return 10
    const longRows = candidates.filter((c) => c.isLong)
    if (longRows.length < 2) return 10
    const referenceMidpoint = longRows[0] && longRows[0].midpoint ? longRows[0].midpoint : null
    if (!referenceMidpoint) return 10
    const projectedRows = []
    for (let i = 0; i < longRows.length; i++) {
      const row = longRows[i]
      if (!row || !row.midpoint) continue
      const projection = signedAxisOffsetMeters(referenceMidpoint, row.midpoint, axisUpBearing)
      if (!Number.isFinite(projection)) continue
      projectedRows.push({
        bearing: row.bearing,
        projection: projection
      })
    }
    if (projectedRows.length < 2) return 10
    let minSpacing = Infinity
    for (let i = 0; i < projectedRows.length - 1; i++) {
      for (let j = i + 1; j < projectedRows.length; j++) {
        const a = projectedRows[i]
        const b = projectedRows[j]
        if (axialDiffDeg(a.bearing, b.bearing) > 12) continue
        const projected = Math.abs(a.projection - b.projection)
        if (projected > 0.5 && projected < minSpacing) {
          minSpacing = projected
        }
      }
    }
    if (!Number.isFinite(minSpacing) || minSpacing <= 0) return 10
    return Math.max(0.1, roundToTenth(minSpacing))
  }

  const signedAxisOffsetMeters = function (fromLatLng, toLatLng, axisUpBearing) {
    const map = fnGetMap()
    if (!map || !fromLatLng || !toLatLng || !Number.isFinite(axisUpBearing)) return NaN
    const dist = map.distance(fromLatLng, toLatLng)
    if (!(dist > 0)) return 0
    const bearing = bearingBetweenLatLng(fromLatLng.lat, fromLatLng.lng, toLatLng.lat, toLatLng.lng)
    const axisDiff = angleDiffDeg(bearing, axisUpBearing)
    return dist * Math.cos(axisDiff * (Math.PI / 180))
  }

  const resolveCloneDirectionForTarget = function (candidate, axisUpBearing, targetMode, sourceMode) {
    if (!candidate || !Number.isFinite(axisUpBearing)) return 'up'
    const rows = buildSeekRowCandidates(axisUpBearing)
    const firstRow = pickSeekRowCandidate(rows, 'first')
    const lastRow = pickSeekRowCandidate(rows, 'last')
    if (!firstRow || !lastRow || !firstRow.midpoint || !lastRow.midpoint) return 'up'
    const source = sourceMode === 'last' ? 'last' : 'first'
    const interiorSign = source === 'last'
      ? signedAxisOffsetMeters(lastRow.midpoint, firstRow.midpoint, axisUpBearing)
      : signedAxisOffsetMeters(firstRow.midpoint, lastRow.midpoint, axisUpBearing)
    const interiorDirection = interiorSign >= 0 ? 'up' : 'down'
    if ((targetMode === 'last' ? 'last' : 'first') === source) {
      return interiorDirection === 'up' ? 'down' : 'up'
    }
    return interiorDirection
  }

  const cloneSeekRow = function (candidate, axisUpBearing, targetMode, cloneDistanceMeters, sourceMode) {
    const missionState = fnGetMissionState()
    if (!candidate || !Number.isFinite(axisUpBearing) || !(cloneDistanceMeters > 0)) return false
    if (!missionState || !Array.isArray(missionState.waypoints)) return false
    const points = missionState.waypoints
    const fromIndex = candidate.fromIndex
    const toIndex = candidate.toIndex
    if (fromIndex < 0 || toIndex !== fromIndex + 1 || toIndex >= points.length) return false
    const fromPoint = points[fromIndex]
    const toPoint = points[toIndex]
    if (!fromPoint || !toPoint) return false
    const direction = resolveCloneDirectionForTarget(candidate, axisUpBearing, targetMode, sourceMode)
    const bearing = (direction === 'down') ? ((axisUpBearing + 180) % 360) : axisUpBearing
    const movedFrom = offsetLatLngByMeters(fromPoint.latlng[0], fromPoint.latlng[1], cloneDistanceMeters, bearing)
    const movedTo = offsetLatLngByMeters(toPoint.latlng[0], toPoint.latlng[1], cloneDistanceMeters, bearing)
    const newFar = fnCreateInsertedPoint(toPoint, { lat: movedTo.lat, lng: movedTo.lng })
    const newNear = fnCreateInsertedPoint(fromPoint, { lat: movedFrom.lat, lng: movedFrom.lng })
    if (!newFar || !newNear) return false
    const historyBefore = fnCaptureState()
    if (sourceMode === 'last') {
      missionState.waypoints.splice(toIndex + 1, 0, newFar, newNear)
    } else {
      missionState.waypoints.splice(fromIndex, 0, newFar, newNear)
    }
    fnSyncMissionPlacemarkOrder()
    fnRenderImportLines([missionState.lineCoords || []], missionState.waypoints, fnGetImportKmlFilename() || 'mission', missionState)
    fnHistoryPush(historyBefore)
    fnSelectClear()
    return true
  }

  return {
    buildSeekSegments: buildSeekSegments,
    findSeekRouteBearing: findSeekRouteBearing,
    buildSeekAxisLayerFromBearing: buildSeekAxisLayerFromBearing,
    resolveSeekAxisUpBearing: resolveSeekAxisUpBearing,
    buildAutoSeekAxisLayer: buildAutoSeekAxisLayer,
    getSeekAxisUpBearingForSuggest: getSeekAxisUpBearingForSuggest,
    buildHeadingSuggestModel: buildHeadingSuggestModel,
    buildSeekAxisLayerForPoint: buildSeekAxisLayerForPoint,
    createSeekAxisLayer: createSeekAxisLayer,
    attachSeekAxisLayerToModal: attachSeekAxisLayerToModal,
    buildSeekRowCandidates: buildSeekRowCandidates,
    getImportWaypointsCenter: getImportWaypointsCenter,
    pickSeekRowCandidate: pickSeekRowCandidate,
    estimateSeekCloneDistance: estimateSeekCloneDistance,
    signedAxisOffsetMeters: signedAxisOffsetMeters,
    resolveCloneDirectionForTarget: resolveCloneDirectionForTarget,
    cloneSeekRow: cloneSeekRow
  }
}

export {
  createSeekTools
}
